/*
 * Widget-Katalog des HMI-Editors: ein Eintrag je Widget-Typ, mit Anzeigename,
 * Beschreibung, Gruppe, Standardgroesse im Raster und einer Fabrik, die ein
 * gueltiges Widget mit den Standardwerten erzeugt.
 *
 * Die Standardwerte stammen aus den C#-Records unter
 * `src/core/HVACloud.Core.Hmi.Schema/Widgets.*.cs`. Sie werden hier nicht neu
 * erfunden, weil Server und Editor sonst auseinanderlaufen: der Server
 * deserialisiert ein fehlendes Feld auf den C#-Default, der Editor zeigt
 * seinen eigenen, und der Autor stellt etwas ein, das nach dem Speichern
 * anders aussieht. Wo ein C#-Feld `required` ist und gar keinen Default hat,
 * steht die getroffene Wahl als Kommentar an der Stelle.
 *
 * Diese Datei haelt sich bewusst frei von Laufzeit-Importen (nur `import
 * type`). Sie wird aus dem Node-Testlauf heraus geladen, der TypeScript nur
 * ausduennt und keine Modulaufloesung ohne Dateiendung kennt.
 */

import type {
  ActionButtonWidget,
  HeroValueWidget,
  ImageWidget,
  NavBarWidget,
  ThemeToggleWidget,
  UserInviteWidget,
  AlarmListWidget,
  DialWidget,
  DayScheduleWidget,
  HeatingCurveWidget,
  TankWidget,
  PowerFlowWidget,
  MonthBarsWidget,
  SplitBarWidget,
  ShapeWidget,
  SymbolWidget,
  SchematicPartWidget,
  TextWidget,
  AlarmIndicatorWidget,
  EnergyFlowWidget,
  EnumSetterWidget,
  GaugeWidget,
  HeatCircuitTileWidget,
  HeatPumpKpiWidget,
  HotWaterTileWidget,
  LockGuardWidget,
  MomentaryButtonWidget,
  PvSelfConsumptionWidget,
  RollingCounterWidget,
  RoomThermostatWidget,
  ScheduleEditorWidget,
  SchematicOverlayWidget,
  SetpointStepperWidget,
  SliderWidget,
  SparklineWidget,
  StageCascadeWidget,
  StatusLedWidget,
  ToggleWidget,
  TrendWidget,
  ValueTileWidget,
  WeatherTileWidget,
  WidgetDefinition,
  WidgetKind,
} from '../../widgets';

// ----------------------------------------------------------------- Bindungen

/**
 * Der leere String heisst "noch nicht gebunden".
 *
 * Pflichtbindungen wie `commandPoint: string` sind im TS-Typ nicht optional,
 * ein frisch abgelegtes Widget hat aber noch keinen Datenpunkt. Statt den Typ
 * aufzuweichen (dann muesste jeder Renderer `undefined` behandeln) belegen die
 * Fabriken solche Felder mit `''`.
 *
 * Daraus folgt eine Umrechnung an genau einer Grenze: `BindingField` spricht
 * `string | undefined`, das Widget spricht `string`. Die Umrechnung
 * (`'' -> undefined` hin, `undefined -> ''` zurueck) liegt in `BindingRow` in
 * `properties/fields.tsx` und sonst nirgends. Wer sie an einer zweiten Stelle
 * nachbaut, bekommt Widgets mit `undefined` im JSON, die der Server ablehnt.
 */
export const UNBOUND = '';

// ----------------------------------------------------------------- Gruppen

export type WidgetGroup = 'command' | 'display' | 'hvac' | 'compound' | 'design';

export interface WidgetGroupInfo {
  key: WidgetGroup;
  label: string;
  hint: string;
}

/** Reihenfolge und Beschriftung wie im Widget-Katalog der Vorschau-Seite,
 *  damit dieselben Woerter dieselben Dinge meinen. */
export const WIDGET_GROUPS: readonly WidgetGroupInfo[] = [
  { key: 'command', label: 'Kommando', hint: 'Aktionen, die auf einen Regler schreiben' },
  { key: 'display', label: 'Anzeige', hint: 'Wertanzeige, Zustände, Trends' },
  { key: 'hvac', label: 'HVAC-spezial', hint: 'Anlagenschema, Sankey, Wochenprogramm, Kaskaden' },
  { key: 'compound', label: 'Kacheln', hint: 'Ein ganzes Objekt je Kachel: Heizkreis, Wärmepumpe, Warmwasser, Raum, PV, Wetter' },
  { key: 'design', label: 'Gestaltung', hint: 'Frei gestalten: Knopf mit Skript, Text, großer Wert, Bild, Rohre, Symbole, Navigation' },
];

// ----------------------------------------------------------------- Eintrag

/** Ein Pflichtfeld, das ein frisch abgelegtes Widget noch nicht ausgefuellt
 *  hat. Sichtbar zu markieren, aber niemals ein Grund, das Speichern zu
 *  verbieten: der Editor ist Handarbeit, und wer unterbrochen wird, darf
 *  seinen Zwischenstand nicht verlieren. */
export interface RequiredField {
  field: string;
  label: string;
}

export interface WidgetCatalogEntry<K extends WidgetKind = WidgetKind> {
  kind: K;
  /** Anzeigename in der Palette. */
  title: string;
  /** Wofuer ist das Ding: ein Satz, der dem Autor die Wahl abnimmt. */
  description: string;
  group: WidgetGroup;
  /** Standardgroesse im Raster. Zwoelf Spalten, Zeilenhoehe 80 px im Browser
   *  und 100 px auf den Panels, siehe GRID_SPECS in editorModel.ts. */
  colSpan: number;
  rowSpan: number;
  /** Felder ohne sinnvollen Anfangswert. Siehe {@link RequiredField}. */
  requiredFields: readonly RequiredField[];
  create(id: string): Extract<WidgetDefinition, { kind: K }>;
  /**
   * Ein fertig gebundenes Beispiel fuer die Miniatur in der Palette.
   *
   * Der Unterschied zu {@link create} ist der Zweck und nicht die Form:
   * `create` liefert absichtlich ein ungebundenes Widget, weil der Autor die
   * Datenpunkte selbst waehlt. Eine Palette voller ungebundener Widgets
   * zeigte aber zweiundzwanzig graue Kaesten mit "kein Wert", und genau das
   * ist der Zustand, den der Auftraggeber bemaengelt hat.
   *
   * ALLE Bindungen hier beginnen mit `demo/`, ohne Ausnahme. Das ist die
   * Sperre aus `hmi/bindingKey.ts`: der Live-Adapter loest solche Schluessel
   * nie auf. Sollte ein Beispiel-Widget je versehentlich in ein echtes
   * Projekt geraten, zeigt es sichtbar ins Leere, statt gegen eine echte
   * Anlage zu schreiben. Ein Test haelt das fest.
   *
   * `null` heisst: dieser Typ kann ohne Kundendaten nichts zeigen. Die
   * Palette stellt dann einen Platzhalter hin. Das gibt es genau einmal
   * (Anlagenschema), und die Begruendung steht an der Stelle.
   */
  preview(): Extract<WidgetDefinition, { kind: K }> | null;
}

/**
 * Der Katalog als vollstaendige Abbildung ueber den TS-Union.
 *
 * Absicht: ein neuer Widget-Typ in `hmi/widgets.ts` laesst diese Datei bei
 * `tsc` rot werden, nicht erst die Palette zur Laufzeit leer bleiben. Dazu
 * kommt ein Test, der die Schluessel gegen die Typliste in `widgets.ts` und
 * gegen die `[JsonDerivedType]`-Liste der C#-Seite haelt. Beides zusammen,
 * weil `HmiSchemaExporter.cs` in seinem Kommentar genau so einen Test
 * behauptet, den es nie gab: seine Typliste ist seit den sechs
 * Compound-Typen unvollstaendig, und gemerkt hat es niemand.
 */
type WidgetCatalogMap = { [K in WidgetKind]: WidgetCatalogEntry<K> };

const CATALOG: WidgetCatalogMap = {
  // ------------------------------------------------------------- Kommando

  'momentary-button': {
    kind: 'momentary-button',
    title: 'Impuls-Taster',
    description: 'Schreibt einen Wert, wartet, schreibt zurück. Für Quittieren, Handfahrt, Reset-Leitungen.',
    group: 'command',
    colSpan: 2,
    rowSpan: 1,
    requiredFields: [{ field: 'commandPoint', label: 'Befehls-Datenpunkt' }],
    create: (id): MomentaryButtonWidget => ({
      kind: 'momentary-button',
      id,
      commandPoint: UNBOUND,
      // PulseValue/ReleaseValue sind in C# `required object` ohne Default.
      // true/false ist die haeufigste Impulsleitung; fuer Zaehlregister
      // stellt der Autor 1/0 ein.
      pulseValue: true,
      releaseValue: false,
      pulseDurationMs: 500,
      confirmBeforeWrite: false,
    }),
    preview: (): MomentaryButtonWidget => ({
      kind: 'momentary-button',
      id: 'vorschau-momentary-button',
      label: 'Alarm quittieren',
      commandPoint: 'demo/alarm-count',
      pulseValue: 1,
      releaseValue: 0,
      pulseDurationMs: 500,
      confirmBeforeWrite: false,
    }),
  },

  toggle: {
    kind: 'toggle',
    title: 'Schalter',
    description: 'Ein/Aus mit getrenntem Befehls- und Rückmeldepunkt. Die Stellung folgt der Rückmeldung, damit ein misslungener Schreibvorgang sichtbar bleibt.',
    group: 'command',
    colSpan: 2,
    rowSpan: 1,
    requiredFields: [
      { field: 'commandPoint', label: 'Befehls-Datenpunkt' },
      { field: 'feedbackPoint', label: 'Rückmelde-Datenpunkt' },
    ],
    create: (id): ToggleWidget => ({
      kind: 'toggle',
      id,
      commandPoint: UNBOUND,
      feedbackPoint: UNBOUND,
      // C#-Kommentar: "Defaults let a bool binding use true/false".
      onValue: true,
      offValue: false,
    }),
    preview: (): ToggleWidget => ({
      kind: 'toggle',
      id: 'vorschau-toggle',
      label: 'Kompressor',
      commandPoint: 'demo/compressor',
      feedbackPoint: 'demo/compressor',
      onValue: 'on',
      offValue: 'off',
      onLabel: 'Ein',
      offLabel: 'Aus',
    }),
  },

  'enum-setter': {
    kind: 'enum-setter',
    title: 'Auswahl-Steller',
    description: 'Eine Auswahl aus festen Werten, je nach Anzahl als Segmente, Kacheln oder Liste. Für Betriebsarten und Stufen.',
    group: 'command',
    colSpan: 4,
    rowSpan: 1,
    requiredFields: [
      { field: 'commandPoint', label: 'Befehls-Datenpunkt' },
      { field: 'options', label: 'Mindestens eine Option' },
    ],
    create: (id): EnumSetterWidget => ({
      kind: 'enum-setter',
      id,
      commandPoint: UNBOUND,
      // Leere Liste statt erfundener Optionen: welche Werte der Regler kennt,
      // weiss nur der Autor. Der fehlende Eintrag wird markiert.
      options: [],
      renderHint: 'auto',
    }),
    preview: (): EnumSetterWidget => ({
      kind: 'enum-setter',
      id: 'vorschau-enum-setter',
      label: 'Betriebsart',
      commandPoint: 'demo/mode',
      feedbackPoint: 'demo/mode',
      renderHint: 'auto',
      options: [
        { value: 'heating', label: 'Heizen', colour: '#d0a24c' },
        { value: 'cooling', label: 'Kühlen', colour: '#5aa2ff' },
        { value: 'idle', label: 'Bereit', colour: '#8a929e' },
        { value: 'defrost', label: 'Abtauen', colour: '#d15c5c' },
      ],
    }),
  },

  'setpoint-stepper': {
    kind: 'setpoint-stepper',
    title: 'Sollwert-Steller',
    description: 'Zahlenwert mit Plus/Minus, Grenzen und Schrittweite. Rückmeldung treibt die Anzeige.',
    group: 'command',
    colSpan: 3,
    rowSpan: 2,
    requiredFields: [
      { field: 'commandPoint', label: 'Befehls-Datenpunkt' },
      { field: 'feedbackPoint', label: 'Rückmelde-Datenpunkt' },
    ],
    create: (id): SetpointStepperWidget => ({
      kind: 'setpoint-stepper',
      id,
      commandPoint: UNBOUND,
      feedbackPoint: UNBOUND,
      // Min/Max/Step sind in C# `required` ohne Default. Uebernommen vom
      // Raum-Sollwert der Compound-Kachel (5 / 30 / 0,5), weil das der
      // haeufigste Fall ist und der Autor die Grenzen ohnehin nachzieht.
      min: 5,
      max: 30,
      step: 0.5,
      decimals: 1,
      repeatDelayMs: 250,
    }),
    preview: (): SetpointStepperWidget => ({
      kind: 'setpoint-stepper',
      id: 'vorschau-setpoint-stepper',
      label: 'Raum-Sollwert',
      commandPoint: 'demo/room-setpoint',
      feedbackPoint: 'demo/room-setpoint',
      min: 15,
      max: 28,
      step: 0.5,
      unit: '°C',
      decimals: 1,
    }),
  },

  slider: {
    kind: 'slider',
    title: 'Schieberegler',
    description: 'Stufenloser Wert, geschrieben erst beim Loslassen und nur außerhalb des Totbands.',
    group: 'command',
    colSpan: 4,
    rowSpan: 1,
    requiredFields: [
      { field: 'commandPoint', label: 'Befehls-Datenpunkt' },
      { field: 'feedbackPoint', label: 'Rückmelde-Datenpunkt' },
    ],
    create: (id): SliderWidget => ({
      kind: 'slider',
      id,
      commandPoint: UNBOUND,
      feedbackPoint: UNBOUND,
      // Ohne C#-Default: 0 bis 100 ist der Prozentfall, der am haeufigsten
      // an einem Schieberegler haengt (Leistung, Ventil, Drehzahl).
      min: 0,
      max: 100,
      deadband: 0,
      step: null,
    }),
    preview: (): SliderWidget => ({
      kind: 'slider',
      id: 'vorschau-slider',
      label: 'Pumpenleistung',
      commandPoint: 'demo/pump-load',
      feedbackPoint: 'demo/pump-load',
      min: 0,
      max: 1,
      step: 0.05,
      deadband: 0.02,
    }),
  },

  'lock-guard': {
    kind: 'lock-guard',
    title: 'Freigabe-Riegel',
    description: 'Scharfschalten auf Zeit: ohne Bestätigung fällt die Freigabe nach Ablauf von selbst zurück.',
    group: 'command',
    colSpan: 3,
    rowSpan: 2,
    requiredFields: [
      { field: 'commandPoint', label: 'Befehls-Datenpunkt' },
      { field: 'feedbackPoint', label: 'Rückmelde-Datenpunkt' },
    ],
    create: (id): LockGuardWidget => ({
      kind: 'lock-guard',
      id,
      commandPoint: UNBOUND,
      feedbackPoint: UNBOUND,
      // ArmValue/DisarmValue sind `required object` ohne Default.
      armValue: true,
      disarmValue: false,
      timeoutSeconds: 60,
    }),
    preview: (): LockGuardWidget => ({
      kind: 'lock-guard',
      id: 'vorschau-lock-guard',
      label: 'Handbetrieb',
      commandPoint: 'demo/valve-open',
      feedbackPoint: 'demo/valve-open',
      armValue: true,
      disarmValue: false,
      timeoutSeconds: 60,
      armLabel: 'Freigeben',
      disarmLabel: 'Sperren',
    }),
  },

  // -------------------------------------------------------------- Anzeige

  gauge: {
    kind: 'gauge',
    title: 'Rundinstrument',
    description: 'Ein Messwert auf einer Skala, wahlweise rund, als 240-Grad-Bogen oder als Balken, mit farbigen Bereichen.',
    group: 'display',
    colSpan: 3,
    rowSpan: 3,
    requiredFields: [{ field: 'valuePoint', label: 'Wert-Datenpunkt' }],
    create: (id): GaugeWidget => ({
      kind: 'gauge',
      id,
      valuePoint: UNBOUND,
      // Min/Max `required` ohne Default: 0 bis 100 als neutrale Skala.
      min: 0,
      max: 100,
      decimals: 1,
      style: 'radial',
      zones: [],
    }),
    preview: (): GaugeWidget => ({
      kind: 'gauge',
      id: 'vorschau-gauge',
      label: 'Raumtemperatur',
      valuePoint: 'demo/room-temperature',
      setpointPoint: 'demo/room-setpoint',
      min: 15,
      max: 28,
      unit: '°C',
      decimals: 1,
      style: 'arc240',
      zones: [
        { from: 15, to: 18, tone: 'alarm' },
        { from: 18, to: 20, tone: 'warn' },
        { from: 20, to: 24, tone: 'ok' },
        { from: 24, to: 26, tone: 'warn' },
        { from: 26, to: 28, tone: 'alarm' },
      ],
    }),
  },

  'rolling-counter': {
    kind: 'rolling-counter',
    title: 'Rollzähler',
    description: 'Zählerstand mit laufenden Ziffern. Für Energie, Betriebsstunden, Starts.',
    group: 'display',
    colSpan: 3,
    rowSpan: 1,
    requiredFields: [{ field: 'valuePoint', label: 'Wert-Datenpunkt' }],
    create: (id): RollingCounterWidget => ({
      kind: 'rolling-counter',
      id,
      valuePoint: UNBOUND,
      decimals: 0,
      jumpThreshold: 1000,
      digitWidth: 6,
    }),
    preview: (): RollingCounterWidget => ({
      kind: 'rolling-counter',
      id: 'vorschau-rolling-counter',
      label: 'Energie',
      valuePoint: 'demo/energy-kwh',
      unit: 'kWh',
      decimals: 0,
      digitWidth: 7,
    }),
  },

  'status-led': {
    kind: 'status-led',
    title: 'Zustands-Leuchte',
    description: 'Ein Punkt, dessen Farbe aus einer Wertzuordnung kommt. Für Betrieb, Störung, Betriebsart.',
    group: 'display',
    colSpan: 2,
    rowSpan: 1,
    requiredFields: [
      { field: 'statePoint', label: 'Zustands-Datenpunkt' },
      { field: 'mapping', label: 'Mindestens eine Zuordnung' },
    ],
    create: (id): StatusLedWidget => ({
      kind: 'status-led',
      id,
      statePoint: UNBOUND,
      mapping: [],
      showLabel: true,
    }),
    preview: (): StatusLedWidget => ({
      kind: 'status-led',
      id: 'vorschau-status-led',
      label: 'Betriebsart',
      statePoint: 'demo/mode',
      showLabel: true,
      mapping: [
        { value: 'heating', colour: '#d0a24c', label: 'Heizen' },
        { value: 'cooling', colour: '#5aa2ff', label: 'Kühlen' },
        { value: 'idle', colour: '#8a929e', label: 'Bereit' },
        { value: 'defrost', colour: '#d15c5c', label: 'Abtauung' },
      ],
    }),
  },

  'value-tile': {
    kind: 'value-tile',
    title: 'Wert',
    description: 'Beschriftung, großer Wert und Einheit. Für Temperaturen, Status- und Fehlertexte; Enum-Werte erscheinen als Klartext aus dem Datenpunkt.',
    group: 'display',
    colSpan: 3,
    rowSpan: 1,
    requiredFields: [{ field: 'valuePoint', label: 'Wert-Datenpunkt' }],
    create: (id): ValueTileWidget => ({
      kind: 'value-tile',
      id,
      valuePoint: UNBOUND,
      // ShowAge = true as in the C# record; unit and decimals stay empty on
      // purpose so the leaf's values apply.
      showAge: true,
    }),
    preview: (): ValueTileWidget => ({
      kind: 'value-tile',
      id: 'vorschau-value-tile',
      label: 'Vorlauf',
      valuePoint: 'demo/room-temperature',
      unit: '°C',
      decimals: 1,
      showAge: true,
    }),
  },

  'alarm-indicator': {
    kind: 'alarm-indicator',
    title: 'Alarm-Anzeige',
    description: 'Höchste anstehende Störung als Pille, wahlweise für einen Punkt oder die ganze Anlage.',
    group: 'display',
    colSpan: 2,
    rowSpan: 1,
    requiredFields: [],
    create: (id): AlarmIndicatorWidget => ({
      kind: 'alarm-indicator',
      id,
      scopedToPlant: false,
      showCount: true,
    }),
    preview: (): AlarmIndicatorWidget => ({
      kind: 'alarm-indicator',
      id: 'vorschau-alarm-indicator',
      label: 'Anlagen-Alarme',
      scopedToPlant: false,
      showCount: true,
    }),
  },

  sparkline: {
    kind: 'sparkline',
    title: 'Verlaufsstrich',
    description: 'Kleiner Verlauf der letzten Messwerte, ohne Achsen. Aus dem Ringpuffer, nicht aus der Historie.',
    group: 'display',
    colSpan: 3,
    rowSpan: 1,
    requiredFields: [{ field: 'valuePoint', label: 'Wert-Datenpunkt' }],
    create: (id): SparklineWidget => ({
      kind: 'sparkline',
      id,
      valuePoint: UNBOUND,
      windowSize: 32,
    }),
    preview: (): SparklineWidget => ({
      kind: 'sparkline',
      id: 'vorschau-sparkline',
      label: 'Raumtemperatur',
      valuePoint: 'demo/room-temperature',
      windowSize: 40,
      warnLow: 19,
      warnHigh: 25,
    }),
  },

  trend: {
    kind: 'trend',
    title: 'Trend',
    description: 'Vollständiges Diagramm aus der Historie, mehrere Kurven, mit mitlaufendem Ende.',
    group: 'display',
    colSpan: 8,
    rowSpan: 4,
    requiredFields: [{ field: 'series', label: 'Mindestens eine Kurve' }],
    create: (id): TrendWidget => ({
      kind: 'trend',
      id,
      series: [],
      // C#: TimeSpan.FromHours(24), auf der Leitung als ISO-8601-Dauer.
      defaultRange: 'PT24H',
      defaultAggregation: 'auto',
      liveTail: true,
    }),
    preview: (): TrendWidget => ({
      kind: 'trend',
      id: 'vorschau-trend',
      label: 'Vorlauf und Sollwert',
      defaultRange: 'PT24H',
      liveTail: true,
      series: [
        { point: 'demo/supply-temperature', label: 'Vorlauf', style: 'line' },
        { point: 'demo/room-setpoint', label: 'Sollwert', style: 'stepAfter' },
      ],
    }),
  },

  // --------------------------------------------------------- HVAC-spezial

  'schematic-overlay': {
    kind: 'schematic-overlay',
    title: 'Anlagenschema',
    description: 'Kunden-SVG als Hintergrund, darauf Elemente, die von Datenpunkten gefärbt, gedreht, ein- und ausgeblendet oder beschriftet werden.',
    group: 'hvac',
    colSpan: 12,
    rowSpan: 6,
    requiredFields: [{ field: 'assetRef', label: 'Hintergrund (Asset oder SVG-Id)' }],
    create: (id): SchematicOverlayWidget => ({
      kind: 'schematic-overlay',
      id,
      // Weder assetRef noch svgId: genau eines von beiden muss gesetzt sein,
      // und welches, entscheidet der Autor im Eigenschaftsfeld.
      anims: [],
    }),
    // Einziger Eintrag ohne Vorschau, und der Grund liegt nicht an der
    // Palette: der Hintergrund ist ein Kunden-SVG, das erst zur Laufzeit
    // ueber `/api/hmi/svg/{id}` oder den Asset-Speicher geholt wird. Ein
    // erfundener Verweis kostete eine Anfrage je Miniatur und lieferte einen
    // Ladefehler als Bild. Der Platzhalter sagt mehr.
    preview: () => null,
  },

  'energy-flow': {
    kind: 'energy-flow',
    title: 'Energiefluss',
    description: 'Sankey über Knoten und Verbindungen: Strom, Umweltwärme, Nutzwärme. Die Dicke kommt aus den Datenpunkten.',
    group: 'hvac',
    colSpan: 6,
    rowSpan: 4,
    requiredFields: [
      { field: 'nodes', label: 'Mindestens zwei Knoten' },
      { field: 'links', label: 'Mindestens eine Verbindung' },
    ],
    create: (id): EnergyFlowWidget => ({
      kind: 'energy-flow',
      id,
      nodes: [],
      links: [],
    }),
    preview: (): EnergyFlowWidget => ({
      kind: 'energy-flow',
      id: 'vorschau-energy-flow',
      label: 'Energiefluss',
      copPoint: 'demo/cop',
      powerUnit: 'kW',
      nodes: [
        { id: 'grid', label: 'Netz' },
        { id: 'ambient', label: 'Umwelt' },
        { id: 'heat', label: 'Nutzwärme' },
      ],
      links: [
        { fromNodeId: 'grid', toNodeId: 'heat', valuePoint: 'demo/pump-load' },
        { fromNodeId: 'ambient', toNodeId: 'heat', valuePoint: 'demo/cop' },
      ],
    }),
  },

  'schedule-editor': {
    kind: 'schedule-editor',
    title: 'Wochenprogramm',
    description: 'Heizen und Absenken je Wochentag, mit Ferienkalender. Raster in Minuten je Zelle.',
    group: 'hvac',
    colSpan: 8,
    rowSpan: 4,
    requiredFields: [{ field: 'programPoint', label: 'Programm-Datenpunkt' }],
    create: (id): ScheduleEditorWidget => ({
      kind: 'schedule-editor',
      id,
      programPoint: UNBOUND,
      gridStepMinutes: 15,
      readOnly: false,
    }),
    preview: (): ScheduleEditorWidget => ({
      kind: 'schedule-editor',
      id: 'vorschau-schedule-editor',
      label: 'Wochenprogramm',
      programPoint: 'demo/room-setpoint',
      gridStepMinutes: 30,
      // Nur lesend: die Miniatur ist bedienungsblind, und ein Wochenraster,
      // das aussieht, als koenne man darin ziehen, verspricht zu viel.
      readOnly: true,
    }),
  },

  'stage-cascade': {
    kind: 'stage-cascade',
    title: 'Kaskade',
    description: 'Mehrkesselanlage stufenweise: je Stufe Zustand, Auslastung und Laufzeit.',
    group: 'hvac',
    colSpan: 6,
    rowSpan: 3,
    requiredFields: [{ field: 'stages', label: 'Mindestens eine Stufe' }],
    create: (id): StageCascadeWidget => ({
      kind: 'stage-cascade',
      id,
      stages: [],
    }),
    preview: (): StageCascadeWidget => ({
      kind: 'stage-cascade',
      id: 'vorschau-stage-cascade',
      label: 'Kesselkaskade',
      loadUnit: '%',
      stages: [
        { label: 'Kessel 1', statePoint: 'demo/compressor', loadPoint: 'demo/pump-load' },
        { label: 'Kessel 2', statePoint: 'demo/valve-open', loadPoint: 'demo/pump-load' },
        { label: 'Kessel 3', statePoint: 'demo/mode', loadPoint: 'demo/pump-load' },
      ],
    }),
  },

  // -------------------------------------------------------------- Kacheln

  'heat-circuit-tile': {
    kind: 'heat-circuit-tile',
    title: 'Heizkreis',
    description: 'Ein Heizkreis vollständig: Vorlauf, Rücklauf, Sollwert, Betriebsart, Ventil, Pumpe. Eine Kachel je echter Heizgruppe.',
    group: 'compound',
    colSpan: 4,
    rowSpan: 3,
    requiredFields: [
      { field: 'circuitName', label: 'Name des Heizkreises' },
      { field: 'flowTemperaturePoint', label: 'Vorlauftemperatur' },
      { field: 'setpointPoint', label: 'Sollwert' },
    ],
    create: (id): HeatCircuitTileWidget => ({
      kind: 'heat-circuit-tile',
      id,
      circuitName: '',
      flowTemperaturePoint: UNBOUND,
      setpointPoint: UNBOUND,
      setpointWritable: true,
      setpointMin: 5,
      setpointMax: 30,
      setpointStep: 0.5,
      // `ai` bleibt offen: die C#-Seite setzt ihren eigenen Default-Hinweis,
      // und ein hier mitgeschriebener leerer Block wuerde ihn ueberschreiben.
    }),
    preview: (): HeatCircuitTileWidget => ({
      kind: 'heat-circuit-tile',
      id: 'vorschau-heat-circuit-tile',
      label: 'Heizkreis EG',
      circuitName: 'Erdgeschoss (Fußboden)',
      flowTemperaturePoint: 'demo/supply-temperature',
      returnTemperaturePoint: 'demo/hp/return',
      setpointPoint: 'demo/room-setpoint',
      setpointMin: 5,
      setpointMax: 30,
      setpointStep: 0.5,
      modePoint: 'demo/mode',
      valvePositionPoint: 'demo/hc/valve',
      pumpStatePoint: 'demo/hc/pump',
      roomTemperaturePoint: 'demo/room-temperature',
      outsideTemperaturePoint: 'demo/outside/temp',
    }),
  },

  'heat-pump-kpi': {
    kind: 'heat-pump-kpi',
    title: 'Wärmepumpe',
    description: 'Eine Wärmepumpe auf einen Blick: Leistung, Arbeitszahl, Zustand, Laufzeiten, Starts.',
    group: 'compound',
    colSpan: 4,
    rowSpan: 3,
    requiredFields: [
      { field: 'heatPumpName', label: 'Name der Wärmepumpe' },
      { field: 'electricalPowerPoint', label: 'Elektrische Leistung' },
      { field: 'statePoint', label: 'Betriebszustand' },
    ],
    create: (id): HeatPumpKpiWidget => ({
      kind: 'heat-pump-kpi',
      id,
      heatPumpName: '',
      electricalPowerPoint: UNBOUND,
      statePoint: UNBOUND,
    }),
    preview: (): HeatPumpKpiWidget => ({
      kind: 'heat-pump-kpi',
      id: 'vorschau-heat-pump-kpi',
      label: 'Wärmepumpe',
      heatPumpName: 'Luft/Wasser 10 kW',
      electricalPowerPoint: 'demo/hp/electrical-power',
      thermalPowerPoint: 'demo/hp/thermal-power',
      statePoint: 'demo/hp/state',
      flowTemperaturePoint: 'demo/hp/flow',
      outsideTemperaturePoint: 'demo/outside/temp',
      runtimeTodayPoint: 'demo/hp/runtime-today',
      runtimeLifetimePoint: 'demo/hp/runtime-lifetime',
      compressorStartsTodayPoint: 'demo/hp/starts-today',
    }),
  },

  'hot-water-tile': {
    kind: 'hot-water-tile',
    title: 'Warmwasser',
    description: 'Speicher mit Schichtung, Sollwert, Ladezustand und Legionellen-Termin.',
    group: 'compound',
    colSpan: 3,
    rowSpan: 3,
    requiredFields: [
      { field: 'storageName', label: 'Name des Speichers' },
      { field: 'topTemperaturePoint', label: 'Temperatur oben' },
      { field: 'setpointPoint', label: 'Sollwert' },
    ],
    create: (id): HotWaterTileWidget => ({
      kind: 'hot-water-tile',
      id,
      storageName: '',
      topTemperaturePoint: UNBOUND,
      setpointPoint: UNBOUND,
      setpointWritable: true,
    }),
    preview: (): HotWaterTileWidget => ({
      kind: 'hot-water-tile',
      id: 'vorschau-hot-water-tile',
      label: 'Warmwasser',
      storageName: 'Pufferspeicher 300 L',
      topTemperaturePoint: 'demo/hw/top',
      midTemperaturePoint: 'demo/hw/mid',
      bottomTemperaturePoint: 'demo/hw/bottom',
      setpointPoint: 'demo/hw/setpoint',
      statePoint: 'demo/hw/state',
      nextLegionellaPoint: 'demo/hw/next-legionella',
    }),
  },

  'room-thermostat': {
    kind: 'room-thermostat',
    title: 'Raum',
    description: 'Raumtemperatur, Sollwert, Betriebsart, dazu Feuchte, Fensterkontakt und Präsenz.',
    group: 'compound',
    colSpan: 3,
    rowSpan: 3,
    requiredFields: [
      { field: 'roomName', label: 'Name des Raums' },
      { field: 'roomTemperaturePoint', label: 'Raumtemperatur' },
      { field: 'setpointPoint', label: 'Sollwert' },
    ],
    create: (id): RoomThermostatWidget => ({
      kind: 'room-thermostat',
      id,
      roomName: '',
      roomTemperaturePoint: UNBOUND,
      setpointPoint: UNBOUND,
      setpointWritable: true,
      // Andere Grenzen als beim Heizkreis, und das ist Absicht: die
      // C#-Records tragen 10 / 28 / 0,5 fuer den Raum und 5 / 30 / 0,5 fuer
      // den Kreis.
      setpointMin: 10,
      setpointMax: 28,
      setpointStep: 0.5,
    }),
    preview: (): RoomThermostatWidget => ({
      kind: 'room-thermostat',
      id: 'vorschau-room-thermostat',
      label: 'Wohnzimmer',
      roomName: 'Wohnzimmer',
      roomTemperaturePoint: 'demo/room-temperature',
      setpointPoint: 'demo/room-setpoint',
      setpointMin: 10,
      setpointMax: 28,
      setpointStep: 0.5,
      modePoint: 'demo/mode',
      humidityPoint: 'demo/room/humidity',
      windowContactPoint: 'demo/room/window',
      presencePoint: 'demo/room/presence',
    }),
  },

  'pv-self-consumption': {
    kind: 'pv-self-consumption',
    title: 'Photovoltaik',
    description: 'Erzeugung, Eigenverbrauch, Einspeisung, Netzbezug und Batterie, dazu die Tagessummen.',
    group: 'compound',
    colSpan: 6,
    rowSpan: 3,
    requiredFields: [{ field: 'pvPowerPoint', label: 'PV-Leistung' }],
    create: (id): PvSelfConsumptionWidget => ({
      kind: 'pv-self-consumption',
      id,
      pvPowerPoint: UNBOUND,
    }),
    preview: (): PvSelfConsumptionWidget => ({
      kind: 'pv-self-consumption',
      id: 'vorschau-pv-self-consumption',
      label: 'Photovoltaik',
      pvPowerPoint: 'demo/pv/production',
      householdPowerPoint: 'demo/pv/household',
      gridPowerPoint: 'demo/pv/grid',
      batterySocPoint: 'demo/pv/battery-soc',
      batteryPowerPoint: 'demo/pv/battery-power',
      dailyProductionEnergyPoint: 'demo/pv/daily-production',
      dailySelfUseEnergyPoint: 'demo/pv/daily-self',
      dailyFeedInEnergyPoint: 'demo/pv/daily-feed',
      dailyGridDrawEnergyPoint: 'demo/pv/daily-grid',
    }),
  },

  'weather-tile': {
    kind: 'weather-tile',
    title: 'Außenklima',
    description: 'Außentemperatur roh und gedämpft, Feuchte, Wind. Der Kontext, der das Verhalten der Anlage erklärt.',
    group: 'compound',
    colSpan: 3,
    rowSpan: 2,
    requiredFields: [{ field: 'outsideTemperaturePoint', label: 'Außentemperatur' }],
    create: (id): WeatherTileWidget => ({
      kind: 'weather-tile',
      id,
      outsideTemperaturePoint: UNBOUND,
    }),
    preview: (): WeatherTileWidget => ({
      kind: 'weather-tile',
      id: 'vorschau-weather-tile',
      label: 'Außenklima',
      outsideTemperaturePoint: 'demo/outside/temp',
      attenuatedTemperaturePoint: 'demo/outside/attenuated',
      humidityPoint: 'demo/humidity',
      windSpeedPoint: 'demo/outside/wind',
      weatherIconPoint: 'demo/outside/icon',
    }),
  },

  // ------------------------------------------------------------- Gestaltung
  // docs/hmi-panel-ausbau.md §6. Gedacht fuer die freie Anordnung, laufen
  // aber auch im Raster.

  'action-button': {
    kind: 'action-button',
    title: 'Knopf mit Skript',
    description: 'Führt Aktionen aus: Skript (z. B. Sollwert +0,5), Wert setzen oder Bildschirm wechseln. Halten wiederholt.',
    group: 'design',
    colSpan: 2,
    rowSpan: 1,
    requiredFields: [],
    create: (id): ActionButtonWidget => ({
      kind: 'action-button',
      id,
      text: '+',
      shape: 'circle',
      repeatMs: 400,
      points: {},
      actions: [{ type: 'script', script: 'schreibe(soll, begrenze(runde(wert(soll) + 0.5, 0.5), 15, 28))' }],
    }),
    preview: (): ActionButtonWidget => ({
      kind: 'action-button',
      id: 'vorschau-action-button',
      icon: 'plus',
      shape: 'circle',
      points: { soll: 'demo/room-setpoint' },
      actions: [{ type: 'script', script: 'schreibe(soll, begrenze(runde(wert(soll) + 0.5, 0.5), 15, 28))' }],
    }),
  },

  text: {
    kind: 'text',
    title: 'Text',
    description: 'Überschrift, Beschriftung oder Satz mit Werten: {name} setzt einen benannten Datenpunkt ein.',
    group: 'design',
    colSpan: 3,
    rowSpan: 1,
    requiredFields: [],
    create: (id): TextWidget => ({ kind: 'text', id, content: 'Text', size: 20, weight: 'regular' }),
    preview: (): TextWidget => ({
      kind: 'text',
      id: 'vorschau-text',
      content: 'Raum {raum}',
      size: 20,
      weight: 'medium',
      points: { raum: 'demo/room-temperature' },
    }),
  },

  'hero-value': {
    kind: 'hero-value',
    title: 'Großer Wert',
    description: 'Ein Wert groß wie am Bedienteil, mit Beschriftung und kleinen Nebenwerten (Soll, Außen).',
    group: 'design',
    colSpan: 4,
    rowSpan: 2,
    requiredFields: [{ field: 'valuePoint', label: 'Wert-Datenpunkt' }],
    create: (id): HeroValueWidget => ({ kind: 'hero-value', id, valuePoint: UNBOUND, unit: '°', decimals: 1 }),
    preview: (): HeroValueWidget => ({
      kind: 'hero-value',
      id: 'vorschau-hero-value',
      caption: 'Raumtemperatur',
      valuePoint: 'demo/room-temperature',
      unit: '°',
      decimals: 1,
      sideValues: [{ label: 'Soll', point: 'demo/room-setpoint', unit: '°' }],
    }),
  },

  image: {
    kind: 'image',
    title: 'Bild',
    description: 'Hochgeladenes Bild (SVG, PNG, JPEG, WebP): Logo, Foto der Anlage, Hintergrundgrafik.',
    group: 'design',
    colSpan: 3,
    rowSpan: 2,
    requiredFields: [{ field: 'assetRef', label: 'Bild' }],
    create: (id): ImageWidget => ({ kind: 'image', id, assetRef: UNBOUND, fit: 'contain' }),
    // Ohne hochgeladenes Bild gibt es nichts zu zeigen.
    preview: () => null,
  },

  shape: {
    kind: 'shape',
    title: 'Form und Rohr',
    description: 'Rechteck, Kreis, Linie oder Rohr. Ein Rohr zeigt fließendes Medium, solange seine Bedingung gilt.',
    group: 'design',
    colSpan: 3,
    rowSpan: 1,
    requiredFields: [],
    create: (id): ShapeWidget => ({ kind: 'shape', id, shape: 'pipe', strokeWidth: 10, flowWhen: '' }),
    preview: (): ShapeWidget => ({
      kind: 'shape',
      id: 'vorschau-shape',
      shape: 'pipe',
      strokeWidth: 10,
      stroke: '#e0393f',
      flowWhen: 'true',
    }),
  },

  symbol: {
    kind: 'symbol',
    title: 'Symbol',
    description: 'Wärmepumpe, Pumpe, Ventilator, Ventil, Speicher und mehr. Läuft und färbt sich nach Zustand.',
    group: 'design',
    colSpan: 2,
    rowSpan: 2,
    requiredFields: [],
    create: (id): SymbolWidget => ({ kind: 'symbol', id, symbol: 'pump', runningWhen: '' }),
    preview: (): SymbolWidget => ({ kind: 'symbol', id: 'vorschau-symbol', symbol: 'heat-pump', runningWhen: 'true' }),
  },

  'schematic-part': {
    kind: 'schematic-part',
    title: 'Teilschema',
    description: 'Teilschema der S400-Anwendung: Speicher, Kreis oder Erzeuger mit Pumpen, Fühlern und Wertfeldern. Ausgewählt wird es im Reiter S400-Teilschemen.',
    group: 'hvac',
    // Die Karten der Vorlage sind 458 x 330 px. Fuenf Spalten zu 100 px und
    // vier Zeilen zu 80 px kommen dem am naechsten.
    colSpan: 5,
    rowSpan: 4,
    // `part` hat eine Vorgabe; die Felder bindet der Autor, keines ist Pflicht,
    // weil ein Teilschema auch mit halber Belegung etwas zeigt.
    requiredFields: [],
    create: (id): SchematicPartWidget => ({ kind: 'schematic-part', id, part: 's400/S3K', bindings: {} }),
    preview: (): SchematicPartWidget => ({ kind: 'schematic-part', id: 'vorschau-schematic-part', part: 's400/S3K', bindings: {} }),
  },

  'nav-bar': {
    kind: 'nav-bar',
    title: 'Navigation',
    description: 'Leiste über die Bildschirme des Projekts, wie die Reiter am Panel. Zeigt, wo man gerade ist.',
    group: 'design',
    colSpan: 6,
    rowSpan: 1,
    requiredFields: [],
    create: (id): NavBarWidget => ({ kind: 'nav-bar', id, showIcons: true, showLabels: true }),
    preview: (): NavBarWidget => ({ kind: 'nav-bar', id: 'vorschau-nav-bar', showIcons: true, showLabels: true }),
  },

  'theme-toggle': {
    kind: 'theme-toggle',
    title: 'Hell/Dunkel',
    description: 'Schaltet die Oberfläche zwischen hell und dunkel. Jedes Gerät merkt sich seine Wahl.',
    group: 'design',
    colSpan: 1,
    rowSpan: 1,
    requiredFields: [],
    create: (id): ThemeToggleWidget => ({ kind: 'theme-toggle', id }),
    preview: (): ThemeToggleWidget => ({ kind: 'theme-toggle', id: 'vorschau-theme-toggle' }),
  },

  'user-invite': {
    kind: 'user-invite',
    title: 'Benutzer hinzufügen',
    description: 'Zeigt am Bedienfeld einen QR-Code, mit dem sich jemand für die Fernbedienung dieser Anlage registriert. Erscheint nur am Bedienfeld, nie in der Web-HMI.',
    group: 'design',
    colSpan: 2,
    rowSpan: 1,
    requiredFields: [],
    create: (id): UserInviteWidget => ({ kind: 'user-invite', id, localOnly: true }),
    preview: (): UserInviteWidget => ({ kind: 'user-invite', id: 'vorschau-user-invite', localOnly: true }),
  },

  'alarm-list': {
    kind: 'alarm-list',
    title: 'Meldungsliste',
    description: 'Einzelalarme eines Reglers. Zeigt nur, was gerade ansteht, sonst „Keine Meldungen“.',
    group: 'design',
    colSpan: 4,
    rowSpan: 3,
    requiredFields: [],
    create: (id): AlarmListWidget => ({ kind: 'alarm-list', id, title: 'Meldungen', entries: [], points: {} }),
    preview: (): AlarmListWidget => ({
      kind: 'alarm-list', id: 'vorschau-alarm-list', title: 'Meldungen',
      entries: [{ point: 'a', label: 'Fühler Außen' }], points: {},
    }),
  },

  dial: {
    kind: 'dial',
    title: 'Bogenskala',
    description: 'Sollwert auf einem Bogen über 270 Grad, mit Merker für den Istwert. Plus und Minus als Knöpfe daneben.',
    group: 'design',
    colSpan: 4,
    rowSpan: 3,
    requiredFields: [{ field: 'valuePoint', label: 'Wert-Datenpunkt' }],
    create: (id): DialWidget => ({ kind: 'dial', id, valuePoint: UNBOUND, min: 15, max: 28, unit: '°', decimals: 1 }),
    preview: (): DialWidget => ({
      kind: 'dial', id: 'vorschau-dial', caption: 'Komfort', valuePoint: 'demo/room-setpoint',
      markerPoint: 'demo/room-temperature', markerLabel: 'Raum', min: 15, max: 28, unit: '°', decimals: 1,
    }),
  },

  'day-schedule': {
    kind: 'day-schedule',
    title: 'Zeitprogramm heute',
    description: 'Komfortzeiten des heutigen Tages als Balken, mit Jetzt-Strich und nächstem Wechsel.',
    group: 'design',
    colSpan: 6,
    rowSpan: 2,
    requiredFields: [{ field: 'mondayPoint', label: 'Montag' }],
    create: (id): DayScheduleWidget => ({
      kind: 'day-schedule', id, mondayPoint: UNBOUND, tuesdayPoint: UNBOUND, wednesdayPoint: UNBOUND,
      thursdayPoint: UNBOUND, fridayPoint: UNBOUND, saturdayPoint: UNBOUND, sundayPoint: UNBOUND, autoValue: 1,
    }),
    preview: (): DayScheduleWidget => ({
      kind: 'day-schedule', id: 'vorschau-day-schedule', mondayPoint: 'demo/zsp', tuesdayPoint: 'demo/zsp',
      wednesdayPoint: 'demo/zsp', thursdayPoint: 'demo/zsp', fridayPoint: 'demo/zsp', saturdayPoint: 'demo/zsp',
      sundayPoint: 'demo/zsp',
    }),
  },

  'heating-curve': {
    kind: 'heating-curve',
    title: 'Heizkennlinie',
    description: 'Vorlaufsollwert über der Außentemperatur, mit dem Punkt von jetzt und der Sommergrenze.',
    group: 'design',
    colSpan: 6,
    rowSpan: 3,
    requiredFields: [{ field: 'slopePoint', label: 'Steilheit' }],
    create: (id): HeatingCurveWidget => ({ kind: 'heating-curve', id, slopePoint: UNBOUND }),
    preview: (): HeatingCurveWidget => ({ kind: 'heating-curve', id: 'vorschau-heating-curve', slopePoint: 'demo/slope' }),
  },

  tank: {
    kind: 'tank',
    title: 'Speicher',
    description: 'Speicher mit Temperaturschichtung von oben nach unten, auf Wunsch mit Ladeanzeige.',
    group: 'design',
    colSpan: 2,
    rowSpan: 3,
    requiredFields: [{ field: 'topPoint', label: 'Temperatur oben' }],
    create: (id): TankWidget => ({ kind: 'tank', id, topPoint: UNBOUND }),
    preview: (): TankWidget => ({ kind: 'tank', id: 'vorschau-tank', topPoint: 'demo/dhw-top', caption: 'Warmwasser' }),
  },

  'power-flow': {
    kind: 'power-flow',
    title: 'Energiefluss',
    description: 'Strom links, Arbeitszahl im Ring, Wärme rechts. Die Striche laufen, solange Wärme abgegeben wird.',
    group: 'design',
    colSpan: 6,
    rowSpan: 2,
    requiredFields: [{ field: 'elPoint', label: 'Strom' }, { field: 'heatPoint', label: 'Wärme' }],
    create: (id): PowerFlowWidget => ({ kind: 'power-flow', id, elPoint: UNBOUND, heatPoint: UNBOUND }),
    preview: (): PowerFlowWidget => ({ kind: 'power-flow', id: 'vorschau-power-flow', elPoint: 'demo/el-power', heatPoint: 'demo/heat-power' }),
  },

  'month-bars': {
    kind: 'month-bars',
    title: 'Monatsbalken',
    description: 'Zwölf Monate als Balken, etwa die Arbeitszahl je Monat, mit Bezugslinie für das Jahr.',
    group: 'design',
    colSpan: 6,
    rowSpan: 3,
    requiredFields: [{ field: 'valuesPoint', label: 'Monatswerte' }],
    create: (id): MonthBarsWidget => ({ kind: 'month-bars', id, valuesPoint: UNBOUND, title: 'Arbeitszahl je Monat' }),
    preview: (): MonthBarsWidget => ({ kind: 'month-bars', id: 'vorschau-month-bars', valuesPoint: 'demo/months', title: 'Arbeitszahl je Monat' }),
  },

  'split-bar': {
    kind: 'split-bar',
    title: 'Verhältnisbalken',
    description: 'Zwei Mengen als ein Balken, etwa Strom gegen Wärme im laufenden Jahr.',
    group: 'design',
    colSpan: 6,
    rowSpan: 2,
    requiredFields: [{ field: 'aPoint', label: 'Erste Menge' }, { field: 'bPoint', label: 'Zweite Menge' }],
    create: (id): SplitBarWidget => ({ kind: 'split-bar', id, aPoint: UNBOUND, bPoint: UNBOUND }),
    preview: (): SplitBarWidget => ({ kind: 'split-bar', id: 'vorschau-split-bar', aPoint: 'demo/year-elec', bPoint: 'demo/year-heat' }),
  },
};

export const WIDGET_CATALOG: Readonly<WidgetCatalogMap> = CATALOG;

/** Alle Eintraege, nach Gruppenreihenfolge und innerhalb der Gruppe in der
 *  Reihenfolge der Deklaration. Die Palette rendert diese Liste. */
export const WIDGET_CATALOG_LIST: readonly WidgetCatalogEntry[] = (() => {
  const all = Object.values(CATALOG) as WidgetCatalogEntry[];
  const order = new Map(WIDGET_GROUPS.map((g, i) => [g.key, i]));
  return all
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => {
      const ga = order.get(a.entry.group) ?? 99;
      const gb = order.get(b.entry.group) ?? 99;
      return ga === gb ? a.index - b.index : ga - gb;
    })
    .map((x) => x.entry);
})();

export function catalogEntry(kind: WidgetKind): WidgetCatalogEntry {
  return CATALOG[kind] as WidgetCatalogEntry;
}

/**
 * Fabrik ueber den Katalog. Erzeugt ein Widget, das vollstaendig genug ist,
 * um gespeichert und gerendert zu werden, aber nicht vollstaendig genug, um
 * ohne Zutun des Autors etwas Sinnvolles zu zeigen: die Bindungen fehlen
 * absichtlich, siehe {@link UNBOUND}.
 */
export function createWidget(kind: WidgetKind, id: string): WidgetDefinition {
  return CATALOG[kind].create(id);
}

/**
 * Das Beispiel-Widget einer Art, oder `null`, wenn es keines gibt.
 *
 * Siehe {@link WidgetCatalogEntry.preview}. Der Weg ueber diese Funktion
 * statt ueber den Eintrag ist fuer Aufrufer gedacht, die nur die Art in der
 * Hand haben; die Palette hat den Eintrag ohnehin.
 */
export function previewWidget(kind: WidgetKind): WidgetDefinition | null {
  return CATALOG[kind].preview();
}

/**
 * Welche Pflichtfelder noch offen sind.
 *
 * Nur zum Markieren gedacht. Ein halbfertiges Widget bleibt speicherbar, und
 * diese Funktion darf nie zur Bedingung fuer irgendetwas werden, was der
 * Autor sonst nicht mehr tun kann.
 */
export function missingRequiredFields(widget: WidgetDefinition): readonly RequiredField[] {
  const entry = catalogEntry(widget.kind);
  const bag = widget as unknown as Record<string, unknown>;
  return entry.requiredFields.filter((req) => {
    // Sonderfall Anlagenschema: der Hintergrund darf aus dem Asset-Speicher
    // ODER aus der alten, projekteigenen SVG-Id kommen, genau eines von
    // beiden. Ein einzelner Feldname trifft das nicht.
    if (widget.kind === 'schematic-overlay' && req.field === 'assetRef') {
      return !widget.assetRef?.assetId && !widget.svgId;
    }
    // Monatsbalken und Jahresbalken duerfen statt eines Punkts Zaehlerlisten
    // tragen (heatPoints/elPoints, aPoints/bPoints).
    if (widget.kind === 'month-bars' && req.field === 'valuesPoint' && widget.heatPoints?.length) return false;
    if (widget.kind === 'split-bar' && req.field === 'aPoint' && widget.aPoints?.length) return false;
    if (widget.kind === 'split-bar' && req.field === 'bPoint' && widget.bPoints?.length) return false;
    const value = bag[req.field];
    if (value === undefined || value === null) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (Array.isArray(value)) {
      // Ein Sankey mit einem Knoten ist kein Fluss. Der Rest genuegt sich
      // mit einem Eintrag.
      const least = widget.kind === 'energy-flow' && req.field === 'nodes' ? 2 : 1;
      return value.length < least;
    }
    return false;
  });
}
